import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { execute } from '@/graphql/execute';
import { getProjectJobsQuery } from '@/queries/project.query';
import { batchByproductsQuery } from '@/queries/byproduct.query';
import { reportTrackingAtom } from '@/atoms/report-progress.atom';

/**
 * Mounted once per project shell (alongside `useVectorExportProgress`). A
 * PDF_REPORT job produces a download-only byproduct (no viewable layer), so
 * success is detected by the parent job transitioning to PROCESSED. On success
 * the loading toast resolves into a success toast carrying a Download action
 * that lazily resolves the byproduct's presigned URL by `processJobId`.
 */
export function useReportProgress(projectId: string) {
  const { t } = useTranslation('project');
  const [trackers, setTrackers] = useAtom(reportTrackingAtom);

  const projectTrackers = trackers.filter((tr) => tr.projectId === projectId);
  const hasTrackers = projectTrackers.length > 0;

  const { data: jobsData, refetch: refetchJobs } = useQuery({
    queryKey: ['projectJobs', projectId],
    queryFn: () => execute(getProjectJobsQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });

  useEffect(() => {
    if (!hasTrackers || !projectId) return;
    const tick = () => void refetchJobs();
    tick();
    const id = window.setInterval(tick, 2000);
    return () => window.clearInterval(id);
  }, [hasTrackers, projectId, refetchJobs]);

  useEffect(() => {
    if (!hasTrackers) return;
    const jobs = jobsData?.jobs ?? [];

    setTrackers((prev) => {
      const next = [...prev];
      let changed = false;

      for (let i = next.length - 1; i >= 0; i--) {
        const tracker = next[i];
        if (tracker.projectId !== projectId) continue;

        // PDF_REPORT is the only leaf in its synthetic batch, so matching by
        // batchId (and type, defensively) is unambiguous.
        const jobItem = jobs.find((j) => j.batchId === tracker.batchId && j.type === 'PDF_REPORT');
        if (!jobItem) continue;

        if (jobItem.status === 'FAILED') {
          const detail =
            typeof jobItem.errorLogs === 'string' && jobItem.errorLogs.trim().length > 0
              ? jobItem.errorLogs.trim()
              : null;
          toast.error(detail ?? t('report.failedToast'), {
            // Only attach `id` when present — without it sonner creates a
            // new toast (the deviation-modal path skips the loading toast).
            ...(tracker.toastId !== undefined ? { id: tracker.toastId } : {}),
            duration: 6000,
          });
          next.splice(i, 1);
          changed = true;
          continue;
        }

        if (jobItem.status === 'PROCESSED') {
          const completedJobId = jobItem.id;
          toast.success(t('report.completedToast', { title: tracker.title }), {
            // Only attach `id` when present — without it sonner creates a
            // new toast (the deviation-modal path skips the loading toast).
            ...(tracker.toastId !== undefined ? { id: tracker.toastId } : {}),
            duration: 10000,
            action: {
              label: t('report.download'),
              onClick: async () => {
                try {
                  // Resolve via the synthetic batch (by batchId). Match on
                  // processJobId, else take the batch's single export byproduct.
                  const data = await execute(batchByproductsQuery, { id: tracker.batchId });
                  const bps = data.batch?.byproducts ?? [];
                  const bp = bps.find((b) => b.processJobId === completedJobId) ?? bps[0];
                  if (bp) window.open(bp.downloadUrl, '_blank');
                  else toast.error(t('report.byproductNotFound'));
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : String(err));
                }
              },
            },
          });
          next.splice(i, 1);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [jobsData, hasTrackers, projectId, setTrackers, t]);
}
